import { MODULE_ROLE_RANK, type ModuleKey, type ModuleRole, type UserDto } from '@nova/shared';
import { useQueryClient } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { api, ApiError } from '../lib/api.js';

interface MeResponse {
  user: UserDto;
  demoMode: boolean;
}

interface AuthContextValue {
  user: UserDto | null;
  demoMode: boolean;
  status: 'loading' | 'authenticated' | 'anonymous';
  /** Does the signed-in user have this module, at least at `minRole`? */
  hasModule: (module: ModuleKey, minRole?: ModuleRole) => boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
  forgotPassword: (email: string) => Promise<void>;
  resetPassword: (token: string, newPassword: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<UserDto | null>(null);
  // Default off: only /auth/me (or a login) confirming DEMO_MODE turns it on, so
  // a hiccup fetching the session never raises a false «Demo-modus» in prod.
  const [demoMode, setDemoMode] = useState(false);
  const [status, setStatus] = useState<AuthContextValue['status']>('loading');
  const queryClient = useQueryClient();

  useEffect(() => {
    let cancelled = false;

    api
      .get<MeResponse>('/auth/me')
      .then((data) => {
        if (cancelled) return;
        setUser(data.user);
        setDemoMode(data.demoMode);
        setStatus('authenticated');
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        // 401 simply means "not signed in yet" — not an error worth showing.
        if (!(error instanceof ApiError) || error.status !== 401) {
          console.warn('Klarte ikke å hente innlogget bruker:', error);
        }
        setStatus('anonymous');
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const data = await api.post<{ user: UserDto; demoMode: boolean }>('/auth/login', {
      email,
      password,
    });
    setUser(data.user);
    setDemoMode(data.demoMode);
    setStatus('authenticated');
  }, []);

  const logout = useCallback(async () => {
    await api.post('/auth/logout');
    setUser(null);
    setStatus('anonymous');
    queryClient.clear();
  }, [queryClient]);

  // The server rotates the session cookie on success, so this device stays
  // signed in — only other devices are logged out. No client state to update.
  const changePassword = useCallback(async (currentPassword: string, newPassword: string) => {
    await api.post('/auth/change-password', { currentPassword, newPassword });
  }, []);

  // Both are pre-login flows — no session to touch. The server always answers
  // the same to forgotPassword, so this never reveals whether an account exists.
  const forgotPassword = useCallback(async (email: string) => {
    await api.post('/auth/forgot-password', { email });
  }, []);

  const resetPassword = useCallback(async (token: string, newPassword: string) => {
    await api.post('/auth/reset-password', { token, newPassword });
  }, []);

  const hasModule = useCallback(
    (module: ModuleKey, minRole: ModuleRole = 'VIEWER') => {
      const access = user?.modules?.find((m) => m.module === module);
      if (!access) return false;
      return MODULE_ROLE_RANK[access.role] >= MODULE_ROLE_RANK[minRole];
    },
    [user],
  );

  const value = useMemo(
    () => ({
      user,
      demoMode,
      status,
      hasModule,
      login,
      logout,
      changePassword,
      forgotPassword,
      resetPassword,
    }),
    [
      user,
      demoMode,
      status,
      hasModule,
      login,
      logout,
      changePassword,
      forgotPassword,
      resetPassword,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth må brukes inne i AuthProvider.');
  return context;
}
